import React, { useState, useMemo } from 'react';
import { useGoldPrice } from '../hooks/useGoldPrice';
import {
  CALCULATION_CONSTANTS,
  EXPANSION_PLAN,
  DIVIDEND_PROJECTIONS,
  DIVIDEND_DISCLAIMER,
  computeDynamicAnnualProjection
} from '../lib/services/calculationService';
import { useLiveTotalSharesSold } from '../lib/hooks/useLiveTotalSharesSold';
import { InfoTooltip, TERM_EXPLANATIONS } from '../components/common/InfoTooltip';
import {AnimatedNumber, AnimatedCurrency} from '../components/common/AnimatedNumber';
import { DividendProjections2026to2035 } from '../components/dividends/DividendProjections2026to2035';
import { GoldPriceHistoricalChart } from '../components/calculator/GoldPriceHistoricalChart';
import { DIVIDENDS_USER_VISIBLE } from '../lib/dividendsUserVisible';
import '../styles/CalculatorPage.css';

interface FinancialDataPageProps {
  onNavigate: (page: string) => void;
}

const FinancialDataPage: React.FC<FinancialDataPageProps> = ({ onNavigate }) => {
  const [activeTable, setActiveTable] = useState<string>('production');
  const [userShares, setUserShares] = useState<number>(100);

  // Get live gold price
  const { pricePerOunce: liveGoldPricePerOunce, isLive: isGoldPriceLive, source: goldPriceSource } = useGoldPrice();

  // Use live gold price with fallback
  const goldPricePerKg = CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG;
  const { totalSharesSold: liveSharesSold } = useLiveTotalSharesSold();
  const goldPricePerOz = liveGoldPricePerOunce || Math.round(goldPricePerKg / 31.1035);

  // Generate dynamic production data from DIVIDEND_PROJECTIONS
  const productionData = useMemo(() => {
    return Object.entries(DIVIDEND_PROJECTIONS)
      .filter(([year]) => parseInt(year) >= 2026 && parseInt(year) <= 2036)
      .map(([year, projection]) => {
        const yearNum = parseInt(year);
        const expansion = EXPANSION_PLAN[yearNum as keyof typeof EXPANSION_PLAN];
        const goldKg = projection.goldProductionKgPerYear;
        const revenue = goldKg * goldPricePerKg;
        const netKeep = 1 - (CALCULATION_CONSTANTS.REFINING_PERCENT + CALCULATION_CONSTANTS.OPEX_PERCENT) / 100;
        const ebit = revenue * netKeep;
        const dyn = computeDynamicAnnualProjection(yearNum, liveSharesSold);

        return {
          year: yearNum,
          plants: expansion?.plants || 0,
          hectares: expansion?.hectares || 0,
          goldKg: goldKg,
          isRampYear: projection.isRampYear,
          monthsOperating: projection.monthsOperating,
          revenue: revenue,
          ebit: ebit,
          dividendPerShare: dyn?.estimatedDividendPerShare ?? 0,
          totalDividends: ebit
        };
      });
  }, [goldPricePerKg, liveSharesSold]);

  // Calculate totals
  const totals = useMemo(() => {
    return {
      totalGoldKg: productionData.reduce((sum, row) => sum + row.goldKg, 0),
      totalRevenue: productionData.reduce((sum, row) => sum + row.revenue, 0),
      totalEbit: productionData.reduce((sum, row) => sum + row.ebit, 0),
      totalDividends: productionData.reduce((sum, row) => sum + row.totalDividends, 0),
      avgDividendPerShare: productionData.reduce((sum, row) => sum + row.dividendPerShare, 0) / productionData.length
    };
  }, [productionData]);

  // Phase Breakdown Data (from share phases)
  const phaseBreakdown = [
    {
      phase: "Presale",
      price: "$5.00",
      shares: "200,000",
      totalFunds: "$1,000,000",
      operations: "Launch First Wash Plant at Kadoma",
      csr: "Feed 2,000+ Children, Install 4 Boreholes",
      status: 'completed'
    },
    {
      phase: "Phase 1-3",
      price: "$10-$20",
      shares: "375,000",
      totalFunds: "$5,625,000",
      operations: "Complete 3 Wash Plants, Begin Production",
      csr: "Feed 10,000+ Children, Build 6 Classrooms",
      status: 'active'
    },
    {
      phase: "Phase 4-6",
      price: "$25-$35",
      shares: "175,000",
      totalFunds: "$5,250,000",
      operations: "Launch 6th Plant, Expand Operations",
      csr: "Feed 15,000+ Children, Mobile Health Units",
      status: 'upcoming'
    },
    {
      phase: "Phase 7-10",
      price: "$40-$100",
      shares: "200,000",
      totalFunds: "$14,000,000",
      operations: "10 Plants Total, Begin Mutare Expansion",
      csr: "Feed 22,000+ Children, Hospital Development",
      status: 'upcoming'
    },
    {
      phase: "Phase 11-15",
      price: "$200-$600",
      shares: "250,000",
      totalFunds: "$100,000,000",
      operations: "87 Plants, Multi-Site Operations",
      csr: "Feed 150,000+ Children, Regional Hospital",
      status: 'upcoming'
    },
    {
      phase: "Phase 16-19",
      price: "$700-$1,000",
      shares: "200,000",
      totalFunds: "$170,000,000",
      operations: "257 Plants, Multi-Country Expansion",
      csr: "Feed 250,000+ Children, Complete Hospital",
      status: 'upcoming'
    }
  ];

  // Operational Metrics with dynamic values from CALCULATION_CONSTANTS
  const operationalMetrics = [
    {
      metric: "Plant Capacity",
      value: `${CALCULATION_CONSTANTS.PLANT_CAPACITY_TPH} TPH`,
      description: "Tonnes per hour processing capacity per plant",
      icon: "🏭",
      category: "Production"
    },
    {
      metric: "Operating Hours",
      value: `${CALCULATION_CONSTANTS.EFFECTIVE_HOURS_PER_DAY} hours/day`,
      description: "Conservative estimate (58% uptime) - accounts for maintenance, shift changes, and expected downtime",
      icon: "⏱️",
      category: "Production"
    },
    {
      metric: "Operating Days",
      value: `${CALCULATION_CONSTANTS.OPERATING_DAYS_PER_YEAR} days/year`,
      description: "Annual operating days accounting for maintenance",
      icon: "📅",
      category: "Production"
    },
    {
      metric: "Land per Plant",
      value: "25 hectares",
      description: "Land area covered by each wash plant",
      icon: "🌍",
      category: "Land"
    },
    {
      metric: "Recovered Gold Grade",
      value: `${CALCULATION_CONSTANTS.DEFAULT_IN_SITU_GRADE_G_PER_TONNE} g/tonne`,
      description: "Grams of recovered gold per tonne of throughput",
      icon: "💎",
      category: "Geology"
    },
    {
      metric: "Recovery Rate",
      value: `${CALCULATION_CONSTANTS.DEFAULT_RECOVERY_FACTOR_PERCENT}%`,
      description: "Recovery is baked into the recovered grade above",
      icon: "⚙️",
      category: "Efficiency"
    },
    {
      metric: "Bulk Density",
      value: `${CALCULATION_CONSTANTS.BULK_DENSITY_T_PER_M3} T/m³`,
      description: "Weight of gravel per cubic meter",
      icon: "📊",
      category: "Geology"
    },
    {
      metric: "Operating Costs",
      value: `${CALCULATION_CONSTANTS.OPEX_PERCENT}% of revenue`,
      description: "Total operational expenses as percentage of revenue",
      icon: "💰",
      category: "Financial"
    }
  ];

  const tabs = [
    { id: 'production', label: DIVIDENDS_USER_VISIBLE ? 'Production & Dividends' : 'Production', icon: '📊' },
    ...(DIVIDENDS_USER_VISIBLE ? [{ id: 'projections', label: '10-Year Projections', icon: '📈' }] : []),
    { id: 'phases', label: 'Phase Breakdown', icon: '💰' },
    { id: 'operations', label: 'Operational Metrics', icon: '⚙️' }
  ];

  return (
    <div className="page calculator-page-tabbed">
      {/* Enhanced Page Header */}
      <section className="tabbed-page-header">
        <div className="container">
          <div className="breadcrumb">
            <button onClick={() => onNavigate('home')} className="breadcrumb-link">
              Home
            </button>
            <span className="breadcrumb-separator">→</span>
            <span className="breadcrumb-current">Financial Data</span>
          </div>

          <div className="header-content">
            <div className="header-text">
              <h1 className="page-title">Financial Data & Projections</h1>
              <p className="page-subtitle">
                Comprehensive financial tables, production forecasts, and operational metrics
                based on proven mining parameters and conservative projections
              </p>
            </div>
            <div className="header-stats">
              <div className="stat-item">
                <span className="stat-value gold-shimmer">
                  <AnimatedCurrency value={goldPricePerKg / 1000} showDecimals={false} suffix="k" />
                </span>
                <span className="stat-label">
                  Gold Price/kg
                  {isGoldPriceLive && <span className="live-indicator ml-1"><span className="live-dot"></span>Live</span>}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-value">
                  <AnimatedNumber value={totals.totalGoldKg} suffix=" kg" />
                </span>
                <span className="stat-label">10-Year Gold Output</span>
              </div>
              <div className="stat-item">
                <span className="stat-value text-green-400">
                  <AnimatedCurrency value={totals.totalRevenue / 1000000000} suffix="B" />
                </span>
                <span className="stat-label">10-Year Revenue</span>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="tab-navigation">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTable === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTable(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="tab-content-section">
        <div className="container">
          <div className="tab-content">

      {/* Production & Dividends Table */}
      {activeTable === 'production' && (
        <div className="tab-panel">
          <div className="panel-header">
            <h2>📊 Production & Financial Summary</h2>
            <p>Real-time calculations based on live gold prices and operational parameters</p>
          </div>

          {/* Key Metrics Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '32px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(17, 24, 39, 0.95) 100%)',
              border: '2px solid #D4AF37',
              borderRadius: '16px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.9rem', color: '#9CA3AF', marginBottom: '8px' }}>
                Total Gold Production (10 Years)
              </div>
              <div className="gold-shimmer" style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                {totals.totalGoldKg.toLocaleString()} kg
              </div>
              <div style={{ fontSize: '0.8rem', color: '#6B7280', marginTop: '8px' }}>
                ≈ {(totals.totalGoldKg / 1000).toFixed(1)} tonnes
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(17, 24, 39, 0.95) 100%)',
              border: '2px solid #10B981',
              borderRadius: '16px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.9rem', color: '#9CA3AF', marginBottom: '8px' }}>
                Total Revenue (10 Years)
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10B981' }}>
                ${(totals.totalRevenue / 1000000000).toFixed(2)}B
              </div>
              <div style={{ fontSize: '0.8rem', color: '#6B7280', marginTop: '8px' }}>
                Based on current gold price
              </div>
            </div>

            {DIVIDENDS_USER_VISIBLE && (
            <>
            <div style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(17, 24, 39, 0.95) 100%)',
              border: '2px solid #3B82F6',
              borderRadius: '16px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.9rem', color: '#9CA3AF', marginBottom: '8px' }}>
                Total Shareholder Dividends
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3B82F6' }}>
                ${(totals.totalDividends / 1000000000).toFixed(2)}B
              </div>
              <div style={{ fontSize: '0.8rem', color: '#6B7280', marginTop: '8px' }}>
                1.4M shares (2026&ndash;2029) &middot; 3M shares (2030+)
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(17, 24, 39, 0.95) 100%)',
              border: '2px solid #A855F7',
              borderRadius: '16px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.9rem', color: '#9CA3AF', marginBottom: '8px' }}>
                Avg Dividend Per Share
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#A855F7' }}>
                ${totals.avgDividendPerShare.toFixed(2)}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#6B7280', marginTop: '8px' }}>
                Annual average over 10 years
              </div>
            </div>
            </>
            )}
          </div>

          {/* Enhanced Data Table */}
          <div style={{
            background: 'rgba(31, 41, 55, 0.5)',
            border: '1px solid #374151',
            borderRadius: '16px',
            overflow: 'hidden',
            marginBottom: '32px'
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'rgba(212, 175, 55, 0.1)', borderBottom: '2px solid #D4AF37' }}>
                    <th style={{ padding: '16px', textAlign: 'left', color: '#D4AF37', fontWeight: 'bold' }}>Year</th>
                    <th style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF' }}>Plants</th>
                    <th style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF' }}>Hectares</th>
                    <th style={{ padding: '16px', textAlign: 'center', color: '#D4AF37' }}>Gold (kg)</th>
                    <th style={{ padding: '16px', textAlign: 'right', color: '#9CA3AF' }}>Revenue</th>
                    <th style={{ padding: '16px', textAlign: 'right', color: '#9CA3AF' }}>
                      EBIT
                      <InfoTooltip title={TERM_EXPLANATIONS.EBIT.title}>
                        {TERM_EXPLANATIONS.EBIT.text}
                      </InfoTooltip>
                    </th>
                    {DIVIDENDS_USER_VISIBLE && (
                    <th style={{ padding: '16px', textAlign: 'right', color: '#10B981', fontWeight: 'bold' }}>
                      Dividend/Share
                    </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {productionData.map((row, index) => (
                    <tr
                      key={index}
                      style={{
                        borderBottom: '1px solid #374151',
                        background: index % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.2)'
                      }}
                    >
                      <td style={{ padding: '14px 16px', color: '#fff', fontWeight: 'bold' }}>{row.year}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', color: '#D4AF37' }}>{row.plants}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', color: '#9CA3AF' }}>{row.hectares.toLocaleString()}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', color: '#D4AF37', fontWeight: 'bold' }}>
                        {row.goldKg.toLocaleString()}
                        {row.isRampYear && (
                          <div style={{ fontSize: '10px', color: '#9CA3AF', fontStyle: 'italic', fontWeight: 'normal' }}>
                            {row.monthsOperating} operating months
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', color: '#9CA3AF' }}>
                        ${row.revenue >= 1000000000 ? (row.revenue / 1000000000).toFixed(2) + 'B' : (row.revenue / 1000000).toFixed(1) + 'M'}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', color: '#9CA3AF' }}>
                        ${row.ebit >= 1000000000 ? (row.ebit / 1000000000).toFixed(2) + 'B' : (row.ebit / 1000000).toFixed(1) + 'M'}
                      </td>
                      {DIVIDENDS_USER_VISIBLE && (
                      <td style={{ padding: '14px 16px', textAlign: 'right', color: '#10B981', fontWeight: 'bold', fontSize: '1.1rem' }}>
                        ${row.dividendPerShare.toFixed(2)}
                      </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Gold Price Chart */}
          <GoldPriceHistoricalChart
            currentPrice={goldPricePerOz}
            isLive={isGoldPriceLive}
            source={goldPriceSource}
          />

            {/* Enhanced Key Assumptions Section */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(31, 41, 55, 0.9) 0%, rgba(17, 24, 39, 0.95) 100%)',
              border: '1px solid #374151',
              borderRadius: '16px',
              padding: '32px',
              marginTop: '32px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                <div style={{ fontSize: '2rem' }}>📊</div>
                <div>
                  <h3 style={{ color: '#D4AF37', fontSize: '1.4rem', marginBottom: '4px' }}>Key Financial Assumptions</h3>
                  <p style={{ color: '#9CA3AF', fontSize: '0.95rem' }}>Live data from current market conditions</p>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
              }}>
                <div style={{ background: 'rgba(0,0,0,0.3)', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>🥇</div>
                  <div style={{ color: '#6B7280', fontSize: '0.85rem', marginBottom: '4px' }}>Gold Price</div>
                  <div className="gold-shimmer" style={{ fontSize: '1.4rem', fontWeight: 'bold' }}>
                    ${goldPricePerKg.toLocaleString()}/kg
                  </div>
                  <div style={{ color: '#6B7280', fontSize: '0.75rem', marginTop: '4px' }}>
                    {isGoldPriceLive ? '🟢 Live from LBMA' : '📊 Cached price'}
                  </div>
                </div>

                <div style={{ background: 'rgba(0,0,0,0.3)', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>⚙️</div>
                  <div style={{ color: '#6B7280', fontSize: '0.85rem', marginBottom: '4px' }}>Recovery Rate</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#10B981' }}>
                    {CALCULATION_CONSTANTS.DEFAULT_RECOVERY_FACTOR_PERCENT}%
                  </div>
                  <div style={{ color: '#6B7280', fontSize: '0.75rem', marginTop: '4px' }}>Industry-leading efficiency</div>
                </div>

                <div style={{ background: 'rgba(0,0,0,0.3)', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>💰</div>
                  <div style={{ color: '#6B7280', fontSize: '0.85rem', marginBottom: '4px' }}>Operating Costs</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#EF4444' }}>
                    {CALCULATION_CONSTANTS.OPEX_PERCENT}%
                  </div>
                  <div style={{ color: '#6B7280', fontSize: '0.75rem', marginTop: '4px' }}>Of revenue</div>
                </div>

                <div style={{ background: 'rgba(0,0,0,0.3)', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>🎯</div>
                  <div style={{ color: '#6B7280', fontSize: '0.85rem', marginBottom: '4px' }}>Total Shares</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#3B82F6' }}>
                    {(CALCULATION_CONSTANTS.TOTAL_SHARES / 1000000).toFixed(1)}M
                  </div>
                  <div style={{ color: '#6B7280', fontSize: '0.75rem', marginTop: '4px' }}>Across all phases</div>
                </div>
              </div>
            </div>
        </div>
      )}

      {/* 10-Year Projections Tab */}
      {DIVIDENDS_USER_VISIBLE && activeTable === 'projections' && (
        <div className="tab-panel">
          <div className="panel-header">
            <h2>📈 Interactive 10-Year Dividend Projections</h2>
            <p>Detailed year-by-year breakdown with quarterly dividend schedules</p>
          </div>

          {/* Share Input */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(17, 24, 39, 0.95) 100%)',
            border: '2px solid #D4AF37',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '32px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <div>
                <label style={{ color: '#D4AF37', fontSize: '1rem', fontWeight: 'bold' }}>
                  Calculate for your shares:
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  onClick={() => setUserShares(prev => Math.max(1, prev - 100))}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #D4AF37',
                    background: 'transparent',
                    color: '#D4AF37',
                    fontSize: '1.5rem',
                    cursor: 'pointer'
                  }}
                >−</button>
                <input
                  type="number"
                  min="1"
                  max="100000"
                  value={userShares}
                  onChange={(e) => setUserShares(Math.max(1, parseInt(e.target.value) || 1))}
                  style={{
                    width: '120px',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: '2px solid #D4AF37',
                    background: 'rgba(0,0,0,0.5)',
                    color: '#D4AF37',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}
                />
                <button
                  onClick={() => setUserShares(prev => prev + 100)}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #D4AF37',
                    background: 'transparent',
                    color: '#D4AF37',
                    fontSize: '1.5rem',
                    cursor: 'pointer'
                  }}
                >+</button>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[100, 500, 1000, 5000].map(amount => (
                  <button
                    key={amount}
                    onClick={() => setUserShares(amount)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: userShares === amount ? '2px solid #D4AF37' : '1px solid #374151',
                      background: userShares === amount ? 'rgba(212, 175, 55, 0.2)' : 'transparent',
                      color: userShares === amount ? '#D4AF37' : '#9CA3AF',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >{amount.toLocaleString()}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Full Projections Component */}
          <DividendProjections2026to2035
            userShares={userShares}
            variant="full"
            showSharesInput={false}
          />
        </div>
      )}

      {/* Phase Breakdown Table */}
      {activeTable === 'phases' && (
        <div className="tab-panel">
          <div className="panel-header">
            <h2>💰 Share Phase Fund Allocation</h2>
            <p>Detailed breakdown of how funds are allocated across operations and CSR initiatives</p>
          </div>

          {/* Phase Cards Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '24px',
            marginBottom: '32px'
          }}>
            {phaseBreakdown.map((row, index) => (
              <div
                key={index}
                style={{
                  background: row.status === 'completed'
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(17, 24, 39, 0.95) 100%)'
                    : row.status === 'active'
                    ? 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(17, 24, 39, 0.95) 100%)'
                    : 'linear-gradient(135deg, rgba(31, 41, 55, 0.9) 0%, rgba(17, 24, 39, 0.95) 100%)',
                  border: row.status === 'completed'
                    ? '2px solid #10B981'
                    : row.status === 'active'
                    ? '2px solid #D4AF37'
                    : '1px solid #374151',
                  borderRadius: '16px',
                  padding: '24px',
                  position: 'relative'
                }}
              >
                {/* Status Badge */}
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  background: row.status === 'completed' ? 'rgba(16, 185, 129, 0.2)' : row.status === 'active' ? 'rgba(212, 175, 55, 0.2)' : 'rgba(107, 114, 128, 0.2)',
                  color: row.status === 'completed' ? '#10B981' : row.status === 'active' ? '#D4AF37' : '#6B7280'
                }}>
                  {row.status === 'completed' ? '✓ Completed' : row.status === 'active' ? '🔥 Active' : '⏳ Upcoming'}
                </div>

                <h3 style={{ color: row.status === 'active' ? '#D4AF37' : '#fff', fontSize: '1.3rem', marginBottom: '8px' }}>
                  {row.phase}
                </h3>
                <div style={{ color: '#D4AF37', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '16px' }}>
                  {row.price}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ background: 'rgba(0,0,0,0.3)', padding: '12px', borderRadius: '8px' }}>
                    <div style={{ color: '#6B7280', fontSize: '0.75rem' }}>Shares</div>
                    <div style={{ color: '#fff', fontWeight: 'bold' }}>{row.shares}</div>
                  </div>
                  <div style={{ background: 'rgba(0,0,0,0.3)', padding: '12px', borderRadius: '8px' }}>
                    <div style={{ color: '#6B7280', fontSize: '0.75rem' }}>Total Funds</div>
                    <div style={{ color: '#10B981', fontWeight: 'bold' }}>{row.totalFunds}</div>
                  </div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={{ color: '#9CA3AF', fontSize: '0.85rem', marginBottom: '4px' }}>⚙️ Operations:</div>
                  <div style={{ color: '#fff', fontSize: '0.9rem' }}>{row.operations}</div>
                </div>

                <div>
                  <div style={{ color: '#9CA3AF', fontSize: '0.85rem', marginBottom: '4px' }}>🌍 CSR Impact:</div>
                  <div style={{ color: '#10B981', fontSize: '0.9rem' }}>{row.csr}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Enhanced Phase Summary Section */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            marginTop: '32px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(17, 24, 39, 0.95) 100%)',
              border: '2px solid #D4AF37',
              borderRadius: '16px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🎯</div>
              <div style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '8px' }}>Total Funding Capacity</div>
              <div className="gold-shimmer" style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>$295.9M</div>
              <div style={{ color: '#6B7280', fontSize: '0.85rem', marginTop: '8px' }}>Across all 20 phases</div>
              <div style={{ marginTop: '16px', background: 'rgba(0,0,0,0.3)', borderRadius: '8px', height: '8px', overflow: 'hidden' }}>
                <div style={{ width: '15%', height: '100%', background: 'linear-gradient(90deg, #D4AF37, #B8860B)', borderRadius: '8px' }}></div>
              </div>
              <div style={{ color: '#D4AF37', fontSize: '0.75rem', marginTop: '8px' }}>~15% Raised</div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(17, 24, 39, 0.95) 100%)',
              border: '2px solid #3B82F6',
              borderRadius: '16px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '12px' }}>⚙️</div>
              <div style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '8px' }}>Operations Allocation</div>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#3B82F6' }}>~70%</div>
              <div style={{ color: '#6B7280', fontSize: '0.85rem', marginTop: '8px' }}>Mining & Expansion</div>
              <div style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <span style={{ background: 'rgba(59, 130, 246, 0.2)', padding: '4px 12px', borderRadius: '12px', fontSize: '0.75rem', color: '#3B82F6' }}>Equipment 45%</span>
                <span style={{ background: 'rgba(59, 130, 246, 0.2)', padding: '4px 12px', borderRadius: '12px', fontSize: '0.75rem', color: '#3B82F6' }}>OpEx 25%</span>
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(17, 24, 39, 0.95) 100%)',
              border: '2px solid #10B981',
              borderRadius: '16px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🌍</div>
              <div style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '8px' }}>CSR Allocation</div>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#10B981' }}>~30%</div>
              <div style={{ color: '#6B7280', fontSize: '0.85rem', marginTop: '8px' }}>Community Impact</div>
              <div style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <span style={{ background: 'rgba(16, 185, 129, 0.2)', padding: '4px 12px', borderRadius: '12px', fontSize: '0.75rem', color: '#10B981' }}>Community 20%</span>
                <span style={{ background: 'rgba(16, 185, 129, 0.2)', padding: '4px 12px', borderRadius: '12px', fontSize: '0.75rem', color: '#10B981' }}>Environment 10%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Operational Metrics */}
      {activeTable === 'operations' && (
        <div className="tab-panel">
          <div className="panel-header">
            <h2>⚙️ Operational Parameters & Metrics</h2>
            <p>Technical specifications and operational assumptions used in all calculations</p>
          </div>

          {/* Metrics Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
            marginBottom: '32px'
          }}>
            {operationalMetrics.map((metric, index) => (
              <div
                key={index}
                style={{
                  background: 'linear-gradient(135deg, rgba(31, 41, 55, 0.9) 0%, rgba(17, 24, 39, 0.95) 100%)',
                  border: '1px solid #374151',
                  borderRadius: '16px',
                  padding: '24px',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div style={{ fontSize: '2rem' }}>{metric.icon}</div>
                  <span style={{
                    background: 'rgba(212, 175, 55, 0.2)',
                    color: '#D4AF37',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>{metric.category}</span>
                </div>
                <h3 style={{ color: '#fff', fontSize: '1.1rem', marginBottom: '8px' }}>{metric.metric}</h3>
                <div className="gold-shimmer" style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '8px' }}>
                  {metric.value}
                </div>
                <p style={{ color: '#9CA3AF', fontSize: '0.85rem', lineHeight: '1.5' }}>{metric.description}</p>
                <div style={{
                  marginTop: '16px',
                  paddingTop: '12px',
                  borderTop: '1px solid #374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10B981' }}></div>
                  <span style={{ color: '#10B981', fontSize: '0.8rem' }}>Validated</span>
                </div>
              </div>
            ))}
          </div>

          {/* Data Sources Section */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(31, 41, 55, 0.9) 0%, rgba(17, 24, 39, 0.95) 100%)',
            border: '1px solid #374151',
            borderRadius: '16px',
            padding: '32px',
            marginBottom: '32px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <div style={{ fontSize: '2rem' }}>🔍</div>
              <div>
                <h3 style={{ color: '#D4AF37', fontSize: '1.4rem', marginBottom: '4px' }}>Data Sources & Validation</h3>
                <p style={{ color: '#9CA3AF', fontSize: '0.95rem' }}>Comprehensive verification and industry-standard benchmarking</p>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              {[
                { icon: '🔬', title: 'Geological Data', badge: 'Geological', desc: 'Based on comprehensive 2020 geotechnical report confirming site viability', details: [['Report Date', '2020'], ['Confidence', '95%']] },
                { icon: '🏭', title: 'Equipment Specs', badge: 'Equipment', desc: 'Plant capacity based on verified manufacturer specifications', details: [['Manufacturers', '3 Verified'], ['Rating', 'Certified']] },
                { icon: '📊', title: 'Industry Benchmarks', badge: 'Benchmarks', desc: 'Operating costs benchmarked against similar alluvial gold operations', details: [['Sample Size', '25 Ops'], ['Data', '2023-2024']] },
                { icon: '💰', title: 'Market Data', badge: 'Market', desc: 'Gold prices from London Bullion Market Association (LBMA)', details: [['Source', 'LBMA'], ['Updates', 'Real-time']] }
              ].map((source, idx) => (
                <div key={idx} style={{
                  background: 'rgba(0,0,0,0.3)',
                  borderRadius: '12px',
                  padding: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '1.5rem' }}>{source.icon}</div>
                    <span style={{
                      background: 'rgba(212, 175, 55, 0.2)',
                      color: '#D4AF37',
                      padding: '4px 10px',
                      borderRadius: '8px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>{source.badge}</span>
                  </div>
                  <h4 style={{ color: '#fff', fontSize: '1rem', marginBottom: '8px' }}>{source.title}</h4>
                  <p style={{ color: '#9CA3AF', fontSize: '0.85rem', marginBottom: '12px' }}>{source.desc}</p>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    {source.details.map(([label, value], i) => (
                      <div key={i}>
                        <div style={{ color: '#6B7280', fontSize: '0.7rem' }}>{label}</div>
                        <div style={{ color: '#D4AF37', fontSize: '0.85rem', fontWeight: 'bold' }}>{value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Validation Summary */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px'
          }}>
            <div style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid #10B981',
              borderRadius: '12px',
              padding: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              <div style={{ fontSize: '2rem' }}>✅</div>
              <div>
                <h4 style={{ color: '#10B981', marginBottom: '4px' }}>Validation Complete</h4>
                <p style={{ color: '#9CA3AF', fontSize: '0.85rem' }}>All parameters independently verified and cross-referenced</p>
              </div>
            </div>
            <div style={{
              background: 'rgba(212, 175, 55, 0.1)',
              border: '1px solid #D4AF37',
              borderRadius: '12px',
              padding: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              <div style={{ fontSize: '2rem' }}>🎯</div>
              <div>
                <h4 style={{ color: '#D4AF37', marginBottom: '4px' }}>Conservative Estimates</h4>
                <p style={{ color: '#9CA3AF', fontSize: '0.85rem' }}>All projections use conservative estimates for realistic expectations</p>
              </div>
            </div>
          </div>
        </div>
      )}

          </div>
        </div>
      </section>

      {/* Risk Factors Section - Always Visible */}
      <section style={{
        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(17, 24, 39, 0.95) 100%)',
        padding: '48px 0',
        marginTop: '32px'
      }}>
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
            <div style={{ fontSize: '2.5rem' }}>⚠️</div>
            <div>
              <h2 style={{ color: '#EF4444', fontSize: '1.8rem', marginBottom: '4px' }}>Risk Factors & Disclaimers</h2>
              <p style={{ color: '#9CA3AF' }}>Important considerations for potential shareholders</p>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
            marginBottom: '32px'
          }}>
            {[
              { icon: '📈', title: 'Market Risk', severity: 'High', color: '#EF4444', desc: 'Gold prices fluctuate based on global economic conditions, currency movements, and market sentiment.', mitigation: 'Conservative pricing models and diversified operational approach' },
              { icon: '🏭', title: 'Operational Risk', severity: 'Medium', color: '#F59E0B', desc: 'Mining operations may face equipment failures, weather delays, or regulatory changes.', mitigation: 'Preventive maintenance programs and operational contingency planning' },
              { icon: '🌍', title: 'Political Risk', severity: 'Medium', color: '#F59E0B', desc: 'Operations in multiple African countries are subject to political stability and regulatory changes.', mitigation: 'Strong local partnerships and compliance with all regulatory requirements' },
              { icon: '📊', title: 'Performance Risk', severity: 'Low', color: '#10B981', desc: 'Actual recovery rates and operational efficiency may differ from projections.', mitigation: 'Conservative estimates and continuous process optimization' }
            ].map((risk, idx) => (
              <div key={idx} style={{
                background: 'rgba(31, 41, 55, 0.9)',
                border: `1px solid ${risk.color}40`,
                borderRadius: '16px',
                padding: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div style={{ fontSize: '2rem' }}>{risk.icon}</div>
                  <span style={{
                    background: `${risk.color}20`,
                    color: risk.color,
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>{risk.severity} Impact</span>
                </div>
                <h3 style={{ color: '#fff', fontSize: '1.2rem', marginBottom: '12px' }}>{risk.title}</h3>
                <p style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '16px' }}>{risk.desc}</p>
                <div style={{
                  background: 'rgba(0,0,0,0.3)',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <div style={{ color: '#6B7280', fontSize: '0.75rem', marginBottom: '4px' }}>Mitigation:</div>
                  <div style={{ color: '#10B981', fontSize: '0.85rem' }}>{risk.mitigation}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div style={{
            background: 'rgba(31, 41, 55, 0.9)',
            border: '1px solid #374151',
            borderRadius: '16px',
            padding: '32px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{ fontSize: '1.5rem' }}>📋</div>
              <h3 style={{ color: '#D4AF37', fontSize: '1.2rem' }}>Important Shareholder Disclaimer</h3>
            </div>
            <div style={{ color: '#9CA3AF', fontSize: '0.9rem', lineHeight: '1.7' }}>
              <p style={{ marginBottom: '16px' }}>
                <strong style={{ color: '#fff' }}>Share Purchase Disclosure:</strong> {DIVIDENDS_USER_VISIBLE ? DIVIDEND_DISCLAIMER : 'Share purchases involve operational, market, and mining risk. This information is for education only and is not a guarantee of returns.'}
              </p>
              <p style={{ marginBottom: '16px' }}>
                <strong style={{ color: '#fff' }}>Due Diligence Required:</strong> Potential shareholders are strongly advised to conduct independent due diligence,
                review all available documentation, and carefully consider their risk tolerance and financial objectives before making
                any share purchase decisions.
              </p>
              <p>
                <strong style={{ color: '#fff' }}>Regulatory Compliance:</strong> This information is provided for educational purposes and does not constitute
                financial advice. Aureus Alliance Holdings operates in full compliance with applicable securities regulations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {DIVIDENDS_USER_VISIBLE && (
      <section style={{
        background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(17, 24, 39, 0.95) 100%)',
        padding: '48px 0',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 style={{ color: '#D4AF37', fontSize: '2rem', marginBottom: '16px' }}>Ready to Calculate Your Returns?</h2>
          <p style={{ color: '#9CA3AF', fontSize: '1.1rem', marginBottom: '32px', maxWidth: '600px', margin: '0 auto 32px' }}>
            Use our interactive calculator to see personalized dividend projections based on your shareholding
          </p>
          <button
            onClick={() => onNavigate('calculator')}
            style={{
              background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
              color: '#000',
              padding: '16px 48px',
              borderRadius: '12px',
              border: 'none',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            Open Dividend Calculator →
          </button>
        </div>
      </section>
      )}
    </div>
  );
};

export default FinancialDataPage;
